import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import {toast} from "react-toastify";
import {useNavigate} from 'react-router-dom';
// @ts-ignore
import {MagnifyingGlass, Sparkle, Copy, FileText} from '@phosphor-icons/react';
import {getBaseUrlNodeServer} from "../../config";
import {renderMjml} from "../../lib/mjmlPreview";

const PreviewFrame = ({html}) => (
	<div className="relative w-full overflow-hidden bg-slate-50" style={{aspectRatio: '16 / 10'}}>
		<iframe
			title="template-preview"
			srcDoc={html || '<html><body style="margin:0;background:#f8fafc;"></body></html>'}
			sandbox=""
			className="absolute top-0 left-0 pointer-events-none border-0"
			style={{
				transform: 'scale(0.4)',
				transformOrigin: 'top left',
				width: '250%',
				height: '250%',
			}}/>
	</div>
);

const SkeletonCard = () => (
	<div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden animate-pulse">
		<div className="bg-slate-100" style={{aspectRatio: '16 / 10'}}/>
		<div className="p-4">
			<div className="h-4 w-3/4 bg-slate-100 rounded mb-3"/>
			<div className="flex gap-2">
				<div className="h-5 w-14 bg-slate-100 rounded-full"/>
				<div className="h-5 w-16 bg-slate-100 rounded-full"/>
			</div>
		</div>
	</div>
);

const EmptyState = () => (
	<div className="rounded-2xl border border-slate-100 bg-white shadow-sm py-16 px-6 text-center">
		<div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-5">
			<FileText size={32} weight="duotone" color="#10B981"/>
		</div>
		<h3 className="text-xl font-semibold text-slate-900 mb-2">No templates available</h3>
		<p className="text-base text-slate-600 max-w-md mx-auto">
			We&rsquo;re curating a fresh catalog. Check back shortly.
		</p>
	</div>
);

export default function AllTemplates() {
	const [isLoading, setIsLoading] = useState(true);
	const [templates, setTemplates] = useState([]);
	const [search, setSearch] = useState('');
	const [activeCategory, setActiveCategory] = useState('all');
	const [cloningId, setCloningId] = useState(null);
	const navigate = useNavigate();

	useEffect(() => {
		window.scrollTo(0, 0);

		async function loadTemplates() {
			try {
				axios.defaults.baseURL = getBaseUrlNodeServer();
				const response = await axios.post('getusertemplate');
				const usertemplates = Array.isArray(response.data) ? response.data : [];

				const sorted = usertemplates
					.map(t => ({...t, rank: t.rank || 10}))
					.sort((a, b) => a.rank - b.rank);

				// Client-side MJML render via mjml-browser (memoized) — was
				// N HTTP round-trips to /mjml2html (~200–500 ms each); now
				// <50 ms per template, sync, in the user's browser.
				const enriched = sorted.map((template) => ({
					_id: template._id,
					subject: template.subject || 'Untitled template',
					html: renderMjml(template.content || ''),
					rank: template.rank,
					categories: Array.isArray(template.selectedCategories) ? template.selectedCategories : [],
				}));

				setTemplates(enriched);
			} catch (err) {
				console.error(err);
				toast.error('Failed to load templates');
			} finally {
				setIsLoading(false);
			}
		}

		loadTemplates();
	}, []);

	const allCategories = useMemo(() => {
		const set = new Set();
		templates.forEach(t => (t.categories || []).forEach(c => c && set.add(c)));
		return Array.from(set);
	}, [templates]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return templates.filter(t => {
			if (activeCategory !== 'all' && !(t.categories || []).includes(activeCategory)) return false;
			if (q && !(t.subject || '').toLowerCase().includes(q)) return false;
			return true;
		});
	}, [templates, search, activeCategory]);

	async function cloneTemplate(templateId) {
		const signedin = sessionStorage.getItem("signedin");
		const verifiedemail = sessionStorage.getItem("verifiedemail");
		if (!signedin) {
			toast.error("Login to use this template");
			return;
		}
		try {
			setCloningId(templateId);
			if (verifiedemail === 'contact@neugence.ai') {
				// Special privilege: edit the master template directly.
				navigate('/editor/' + templateId);
				return;
			}
			const r = await fetch(getBaseUrlNodeServer() + 'createtemplateduplicate', {
				method: 'post',
				headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({'templateId': templateId, 'verifiedemail': verifiedemail}),
			});
			const result = await r.json();
			const newTemplateId = result.insertedId && result.insertedId.toString();
			if (!newTemplateId) {
				toast.error("Couldn't clone that template");
				return;
			}
			navigate('/editor/' + newTemplateId);
		} catch (err) {
			console.error(err);
			toast.error("Couldn't clone that template");
		} finally {
			setCloningId(null);
		}
	}

	return (
		<div className="bg-white min-h-screen">
			<div className="max-w-7xl mx-auto px-6 py-10">
				{/* Header */}
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
					<div>
						<div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 text-xs font-medium mb-3">
							<Sparkle size={14} weight="fill"/>
							Premium gallery
						</div>
						<h1 className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">Premium templates</h1>
						<p className="text-base text-slate-600 mt-1">
							{isLoading ? 'Loading the gallery…'
								: templates.length === 0 ? 'No templates available right now.'
									: `${templates.length} polished designs ready to clone & customize`}
						</p>
					</div>
				</div>

				{isLoading ? (
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
						{Array.from({length: 6}).map((_, i) => <SkeletonCard key={i}/>)}
					</div>
				) : templates.length === 0 ? (
					<EmptyState/>
				) : (
					<>
						{/* Filters + search */}
						<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
							{allCategories.length > 0 ? (
								<div className="inline-flex flex-wrap gap-1 p-1 rounded-full bg-slate-50 border border-slate-100 self-start">
									<button
										type="button"
										onClick={() => setActiveCategory('all')}
										className={
											'rounded-full px-4 py-1.5 text-sm font-medium transition-colors ' +
											(activeCategory === 'all'
												? 'bg-white text-slate-900 shadow-sm border border-slate-100'
												: 'text-slate-600 hover:text-slate-900')
										}>
										All
										<span className={'ml-2 text-xs ' + (activeCategory === 'all' ? 'text-emerald-600' : 'text-slate-400')}>
											{templates.length}
										</span>
									</button>
									{allCategories.map((cat) => {
										const count = templates.filter(t => (t.categories || []).includes(cat)).length;
										return (
											<button
												key={cat}
												type="button"
												onClick={() => setActiveCategory(cat)}
												className={
													'rounded-full px-4 py-1.5 text-sm font-medium transition-colors ' +
													(activeCategory === cat
														? 'bg-white text-slate-900 shadow-sm border border-slate-100'
														: 'text-slate-600 hover:text-slate-900')
												}>
												{cat}
												<span className={'ml-2 text-xs ' + (activeCategory === cat ? 'text-emerald-600' : 'text-slate-400')}>
													{count}
												</span>
											</button>
										);
									})}
								</div>
							) : <div/>}
							<div className="relative md:w-72">
								<MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
								<input
									type="text"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									placeholder="Search templates…"
									className="w-full pl-9 pr-3 border border-slate-200 rounded-xl py-2.5 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition text-sm"/>
							</div>
						</div>

						{filtered.length === 0 ? (
							<div className="rounded-2xl border border-slate-100 bg-white shadow-sm py-16 px-6 text-center">
								<p className="text-base text-slate-600">No templates match this filter.</p>
							</div>
						) : (
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
								{filtered.map((t) => {
									const isCloning = cloningId === t._id;
									return (
										<div
											key={t._id}
											onClick={() => !isCloning && cloneTemplate(t._id)}
											className="group relative rounded-2xl border border-slate-100 bg-white shadow-sm hover:shadow-lg transition cursor-pointer overflow-hidden">
											<PreviewFrame html={t.html}/>

											{/* Hover overlay */}
											<div className="absolute inset-x-0 top-0 bottom-[88px] flex items-center justify-center gap-3 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition pointer-events-none">
												<button
													type="button"
													disabled={isCloning}
													onClick={(e) => {
														e.stopPropagation();
														cloneTemplate(t._id);
													}}
													className="pointer-events-auto inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white font-semibold shadow-sm transition-colors">
													<Copy size={16} weight="bold"/>
													{isCloning ? 'Cloning…' : 'Use this template'}
												</button>
											</div>

											{/* Footer */}
											<div className="p-4 border-t border-slate-100">
												<div className="font-medium text-slate-900 truncate" title={t.subject}>{t.subject}</div>
												{t.categories && t.categories.length > 0 && (
													<div className="flex flex-wrap gap-1.5 mt-2">
														{t.categories.slice(0, 3).map((c) => (
															<span
																key={c}
																className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-0.5 text-xs font-medium">
																{c}
															</span>
														))}
														{t.categories.length > 3 && (
															<span className="inline-flex items-center text-xs text-slate-500">
																+{t.categories.length - 3}
															</span>
														)}
													</div>
												)}
											</div>
										</div>
									);
								})}
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
